import React, {useEffect, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {TObservablePage} from '../../types/commerce'
import {Layout} from '../../components/layout'
import {Container, Stack} from '../../vanilla'
import {flowResult} from 'mobx'
import {useBasketStore, useContentStore, useCustomerStore, useOrderStore} from '../../store/hooks/useStore'
import {Slot, useContentToDisplay} from '../../components/cms/Slot'
import {SlotContentItem, SlotItem} from '../../types/cms'
import {Seo} from '../../components/seo'
import {BookDelivery} from '../../components/book-delivery'
import {EditOrderStickyBanner} from '../../components/edit-order-sticky-banner'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {usePromotionDataTracking} from '../../analytics/promotion-tracking'
import { HomePageType } from '../../types/cms/navigation'
import { CONTENT_SCHEMAS } from '../../components/cms/utils'

interface HomePageProps {
    content?: HomePageType
    children?: React.ReactNode
  }

const Home: TObservablePage<HomePageProps> = observer(({content: visualizationContent}) => {
  const contentStore = useContentStore()
  const {editMode, recentOrder} = useOrderStore()
  const {currentDeliverySlotId, postcodeError} = useBasketStore()
  const {isSoftLoggedIn, isRegistered} = useCustomerStore()
  const content = visualizationContent ?? contentStore.contentById['homepage']
 
  const sendPageData = usePageDataTracking()
  const pageType = 'storefront'
  let pageCategory = ''

  if (isRegistered) {
    pageCategory = 'Full-login'
  } else if (isSoftLoggedIn()) {
    pageCategory = 'Soft-login'
  } else {
    pageCategory = 'Default'
  }
  const sendPromotionData = usePromotionDataTracking()

  const heroSlots = content?.slots
    // Add index position of slots array for tracking purposes
    .map((slotGroup: SlotContentItem, index: number) => ({ slotGroup, originalIndex: index }))
    .filter(({ slotGroup }: { slotGroup: SlotContentItem; originalIndex: number }) => slotGroup._meta.schema === CONTENT_SCHEMAS.HOME_HERO)
    .map(({ slotGroup, originalIndex }: { slotGroup: SlotContentItem; originalIndex: number }) => {
      const transformedSlots = useContentToDisplay(slotGroup)
      return transformedSlots?.map((slot: SlotItem) => ({
        ...slot,
        slotGroupName: slotGroup._meta.name,
        slotIndex: originalIndex + 1
      })) || []
    }) || []

  const otherSlots = content?.slots
    // Add index position of slots array for tracking purposes
    .map((slotGroup: SlotContentItem, index: number) => ({ slotGroup, originalIndex: index }))
    .filter(({ slotGroup }: { slotGroup: SlotContentItem; originalIndex: number }) => slotGroup._meta.schema !== CONTENT_SCHEMAS.HOME_HERO)
    .map(({ slotGroup, originalIndex }: { slotGroup: SlotContentItem; originalIndex: number }) => {
      const transformedSlots = useContentToDisplay(slotGroup)
      return transformedSlots?.map((slot: SlotItem) => ({
        ...slot,
        slotGroupName: slotGroup._meta.name,
        slotIndex: originalIndex + 1
      })) || []
    }) || []

  let contentToDisplay = [...heroSlots, ...otherSlots]
  contentToDisplay = contentToDisplay.filter(item => item.length > 0)

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [isRegistered, isSoftLoggedIn()])

  useEffect(() => {
    sendPromotionData(contentToDisplay)
  }, [])

  if (!content) {
    return null
  }

  return (
    <Layout>
      {editMode && <EditOrderStickyBanner />}
      <Seo
        title={content?.SEO?.pageTitle || 'Home Page | Iceland'}
        description={content?.SEO?.pageDescription}
      />
      <Stack height={['260px', '260px', '320px']}>
        {heroSlots?.map((slotGroup: SlotContentItem, idx: number) => (
          <Slot key={`${slotGroup?._meta?.deliveryId}-${idx}`} data={slotGroup} />
        ))}
      </Stack>
      <Stack overflow={'auto'} height={currentDeliverySlotId ? ['292px', '122px'] : recentOrder ? ['196px', '96px'] : postcodeError ? ['114px', '122px'] : ['228px', '96px'] }>
        <BookDelivery />
      </Stack>
      <Container paddingBottom={['32px', '48px']}>
        <Stack spacing={['16px', '24px']}>
          {otherSlots?.map((slotGroup: SlotContentItem, idx: number) => (
            <Slot data={slotGroup} key={`${slotGroup?._meta?.deliveryId}-${idx}`} />
          ))}
        </Stack>       
      </Container>
    </Layout>
  )
})

Home.getProps = async ({store}) => {
  const {contentStore} = store
  const {fetchItemByKey} = contentStore
  await flowResult(fetchItemByKey('homepage'))
}

export default Home
